import * as $ from 'jquery';
import { AbstractLogger } from './AbstractLogger';
import { ERROR_CODE } from './ErrorCode';
import { LOG_SOURCE } from './LogSource';
import { ILoggerConfiguration } from '../models/LoggerConfiguration';
import { LOG_LEVEL } from './LogLevel';
import { requestServiceWorkerFlush } from './LoggerServiceWorkerClient';
import { configureLoggerIndexedDb, storeLogsInIndexedDb, flushLogsInIndexedDb, flushLogsInIndexedDbRequest } from './LoggerIndexedDbClient';

/**
 * Logger for AMC's DaVinci Apps. Logs are sent to premise logger only.
 *
 * @export
 * @class PremiseLogger
 * @extends {AbstractLogger}
 */
export class PremiseLogger extends AbstractLogger {
  protected _user: any;

  constructor(logSource: LOG_SOURCE | string, isDev: boolean = false, apiUrl: string = null) {
    super(logSource, isDev, apiUrl, false);
    this._user = this.syncUserId();
  }

  setConfiguration(config: ILoggerConfiguration): void {
    this.config = config;
    this.applyIndexedDbConfig();
  }

  private applyIndexedDbConfig(): void {
    const maxAgeMs = this.getConfiguredPositiveNumber('Max Log Retention');
    const maxLogs = this.getConfiguredPositiveNumber('Max Log Buffer Size');
    const maxFailedLogs = this.getConfiguredPositiveNumber('Max Failed Log Buffer Size');
    AbstractLogger.setLastAppliedConfig(maxAgeMs, maxLogs, maxFailedLogs);
    const serviceWorker = AbstractLogger.serviceWorker;

    if (serviceWorker) {
      serviceWorker.postMessage({
        type: 'CONFIG',
        maxAgeMs,
        maxLogs,
        maxFailedLogs
      });
    } else {
      configureLoggerIndexedDb(maxAgeMs, maxLogs, maxFailedLogs).catch(() => {});
    }
  }

  private logMessage(methodLogLevel: LOG_LEVEL, message: string, errorCode: ERROR_CODE = ERROR_CODE.Other, localTime: Date = new Date()): void {
    const logObj = this.generateLogObj(methodLogLevel, errorCode, message, localTime);
    const routeKey = this.getRouteKey(methodLogLevel);
    const serviceWorker = AbstractLogger.serviceWorker;

    if (serviceWorker) {
      serviceWorker.postMessage({
        type: 'STORE',
        routeKey,
        logs: [logObj]
      });
    } else {
      storeLogsInIndexedDb(routeKey, [logObj]).catch(() => {});
    }
  }

  logLoop(message: string, errorCode: ERROR_CODE, localTime: Date): void {
    if (this.config['Premise Logger URL'] && this.config['Log Level'] <= LOG_LEVEL.Loop) {
      this.logMessage(LOG_LEVEL.Loop, message, errorCode, localTime);
    }
  }

  logTrace(message: string, errorCode?: ERROR_CODE, localTime?: Date): void {
    if (this.config['Premise Logger URL'] && this.config['Log Level'] <= LOG_LEVEL.Trace) {
      this.logMessage(LOG_LEVEL.Trace, message, errorCode, localTime);
    }
  }

  logDebug(message: string, errorCode?: ERROR_CODE, localTime?: Date): void {
    if (this.config['Premise Logger URL'] && this.config['Log Level'] <= LOG_LEVEL.Debug) {
      this.logMessage(LOG_LEVEL.Debug, message, errorCode, localTime);
    }
  }

  logInformation(message: string, errorCode?: ERROR_CODE, localTime?: Date): void {
    if (this.config['Premise Logger URL'] && this.config['Log Level'] <= LOG_LEVEL.Information) {
      this.logMessage(LOG_LEVEL.Information, message, errorCode, localTime);
    }
  }

  logWarning(message: string, errorCode?: ERROR_CODE, localTime?: Date): void {
    if (this.config['Premise Logger URL'] && this.config['Log Level'] <= LOG_LEVEL.Warning) {
      this.logMessage(LOG_LEVEL.Warning, message, errorCode, localTime);
    }
  }

  logError(message: string, errorCode?: ERROR_CODE, localTime?: Date): void {
    if (this.config['Premise Logger URL'] && this.config['Log Level'] <= LOG_LEVEL.Error) {
      this.logMessage(LOG_LEVEL.Error, message, errorCode, localTime);
    }
  }

  logCritical(message: string, errorCode?: ERROR_CODE, localTime?: Date): void {
    if (this.config['Premise Logger URL'] && this.config['Log Level'] <= LOG_LEVEL.Critical) {
      this.logMessage(LOG_LEVEL.Critical, message, errorCode, localTime);
    }
  }

  public pushLogs(): void {
    if (!this.apiUrl) {
      return;
    }
    this.postBuffers(LOG_LEVEL.Loop);
    this.postBuffers(LOG_LEVEL.Trace);
    this.postBuffers(LOG_LEVEL.Debug);
    this.postBuffers(LOG_LEVEL.Information);
    this.postBuffers(LOG_LEVEL.Warning);
    this.postBuffers(LOG_LEVEL.Error);
    this.postBuffers(LOG_LEVEL.Critical);
  }

  private postBuffers(logLevel: LOG_LEVEL): void {
    const loggerEndpoint: string = this.getLoggerEndpoint(logLevel);
    const routeKey = this.getRouteKey(logLevel);
    const serviceWorker = AbstractLogger.serviceWorker;
    this.applyIndexedDbConfig();

    if (serviceWorker) {
      serviceWorker.postMessage({
        type: 'FLUSH',
        routeKey,
        endpoint: loggerEndpoint
      });
    } else {
      flushLogsInIndexedDb(routeKey, loggerEndpoint).catch(() => {});
    }
  }

  public async pushLogsAsync(): Promise<void> {
    if (!this.apiUrl) {
      return;
    }
    await this.postBuffersAsync(LOG_LEVEL.Loop);
    await this.postBuffersAsync(LOG_LEVEL.Trace);
    await this.postBuffersAsync(LOG_LEVEL.Debug);
    await this.postBuffersAsync(LOG_LEVEL.Information);
    await this.postBuffersAsync(LOG_LEVEL.Warning);
    await this.postBuffersAsync(LOG_LEVEL.Error);
    await this.postBuffersAsync(LOG_LEVEL.Critical);
  }

  private async postBuffersAsync(logLevel: LOG_LEVEL): Promise<void> {
    const loggerEndpoint: string = this.getLoggerEndpoint(logLevel);
    const routeKey = this.getRouteKey(logLevel);
    const serviceWorker = AbstractLogger.serviceWorker;
    this.applyIndexedDbConfig();

    if (serviceWorker) {
      await requestServiceWorkerFlush(serviceWorker, routeKey, loggerEndpoint);
      return;
    }

    await flushLogsInIndexedDbRequest(routeKey, loggerEndpoint);
  }

  private getRouteKey(logLevel: LOG_LEVEL): string {
    return `Premise|${this._logSource}|${logLevel.toString()}`;
  }

  private getLoggerEndpoint(logLevel: LOG_LEVEL): string {
    switch (logLevel) {
      case LOG_LEVEL.Loop:
        return `${this.config['Premise Logger URL']}/api/Logger/LogLoop/${this._logSource}/${this.config['Log Level']}/${this._user}`;
      case LOG_LEVEL.Trace:
        return `${this.config['Premise Logger URL']}/api/Logger/LogTrace/${this._logSource}/${this.config['Log Level']}/${this._user}`;
      case LOG_LEVEL.Debug:
        return `${this.config['Premise Logger URL']}/api/Logger/LogDebug/${this._logSource}/${this.config['Log Level']}/${this._user}`;
      case LOG_LEVEL.Information:
        return `${this.config['Premise Logger URL']}/api/Logger/LogInformation/${this._logSource}/${this.config['Log Level']}/${this._user}`;
      case LOG_LEVEL.Warning:
        return `${this.config['Premise Logger URL']}/api/Logger/LogWarning/${this._logSource}/${this.config['Log Level']}/${this._user}`;
      case LOG_LEVEL.Error:
        return `${this.config['Premise Logger URL']}/api/Logger/LogError/${this._logSource}/${this.config['Log Level']}/${this._user}`;
      case LOG_LEVEL.Critical:
        return `${this.config['Premise Logger URL']}/api/Logger/LogCritical/${this._logSource}/${this.config['Log Level']}/${this._user}`;
      default:
        return `${this.config['Premise Logger URL']}/api/Logger/LogTrace/${this._logSource}/${this.config['Log Level']}/${this._user}`;
    }
  }

  /**
   * This method will sync the logLevel with account level log level of the logged in user.
   * User has to be logged in before calling this method.
   */
  protected syncUserId(): void {
    if (this.apiUrl) {
      $.ajax({
        headers: {
          'Content-Type': 'application/json'
        },
        url: `${this.apiUrl}/api/me`,
        xhrFields: {
          withCredentials: true
        },
        type: 'GET',
        success: (user: any) => {
          if (user !== undefined && user != null) {
            this._user = user.userid;
          }
        }
      });
    }
  }
}
